import { useMemo, useState } from "react";
import DatePicker from '../../../../components/DatePicker';
import Loader from '../../../../components/Loader';
import { useDelivery } from '../context/DeliveryContext';
import {
  FiSearch,
  FiChevronDown,
  FiRefreshCw,
  FiFileText,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";

const OUTCOMES = { ACCEPTED: 'Accepted', REJECTED: 'Rejected', PARTIALLY_RECEIVED: 'Partially Received' };

const STATUS_STYLES = {
  Accepted: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-600",
  "Partially Received": "bg-orange-100 text-orange-600",
};

// Temperature colour: green = safe, orange = borderline, red = out of range.
// Adjust thresholds to match your own food-safety rules.
const tempColor = (t) =>
  t <= 5 ? "text-green-600" : t <= 8 ? "text-orange-500" : "text-red-600";

const HEADERS = [
  "Date & Time",
  "Delivery ID",
  "Supplier",
  "Delivery Note / PO",
  "Products",
  "Delivery Temp",
  "Status",
  "Received By",
  "Actions",
];

function FilterSelect({ value, onChange, children }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full appearance-none rounded-lg border border-slate-200 bg-white pl-4 pr-10 text-sm font-medium text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
      >
        {children}
      </select>
      <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-600" />
    </div>
  );
}

function PageButton({ children, active, disabled, onClick, label }) {
  return (
    <button
      aria-label={label}
      aria-current={active ? "page" : undefined}
      disabled={disabled}
      onClick={onClick}
      className={`flex h-9 w-9 items-center justify-center rounded-md border text-sm font-semibold shadow-sm transition-colors ${
        active
          ? "border-blue-600 bg-blue-600 text-white"
          : "border-slate-200 bg-white text-indigo-600 hover:bg-slate-50"
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export default function AllDeliveries() {
  const { deliveries: records, selectedDate, setSelectedDate, onView, loading, error, refresh } = useDelivery();
  const deliveries = useMemo(() => records.map(record => ({
    id: String(record.id),
    date: record.legacySubmissionId ? new Date(`${record.deliveryDate}T00:00:00`).toLocaleDateString('en-GB') : new Date(record.deliveryDateTime).toLocaleDateString('en-GB'),
    time: record.legacySubmissionId ? 'Time not recorded' : new Date(record.deliveryDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    supplier: record.supplier, dn: record.deliveryNote, po: record.poNumber || '—',
    products: record.products.length, temp: record.deliveryTemp == null ? null : Number(record.deliveryTemp),
    status: record.legacySubmissionId ? 'Historical ' + record.status.toLowerCase() : record.status === 'SUBMITTED' ? 'Submitted' : 'Draft',
    outcome: OUTCOMES[record.outcome] || 'Undecided', receivedBy: record.receivedBy?.fullName || 'Unassigned',
  })), [records]);
  const [query, setQuery] = useState("");
  const [supplier, setSupplier] = useState("All Suppliers");
  const [status, setStatus] = useState("All Statuses");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const suppliers = useMemo(
    () => [...new Set(deliveries.map((d) => d.supplier))],
    [deliveries]
  );
  const statuses = useMemo(
    () => [...new Set(deliveries.map((d) => d.status))],
    [deliveries]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return deliveries.filter((d) => {
      const matchesQuery =
        !q ||
        [d.supplier, d.id, d.dn, d.po].some((v) => String(v || '').toLowerCase().includes(q));
      const matchesSupplier =
        supplier === "All Suppliers" || d.supplier === supplier;
      const matchesStatus = status === "All Statuses" || d.status === status;
      return matchesQuery && matchesSupplier && matchesStatus;
    });
  }, [deliveries, query, supplier, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * rowsPerPage;
  const rows = filtered.slice(start, start + rowsPerPage);

  const clearFilters = () => {
    setQuery("");
    setSupplier("All Suppliers");
    setStatus("All Statuses");
    setPage(1);
  };

  const resetPage = (setter) => (v) => {
    setter(v);
    setPage(1);
  };

  return (
    <div className="space-y-4 py-10">
      {/* Filters */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1.2fr_1.5fr_1.5fr_auto] md:items-center">
        <div className="relative">
          <FiSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-indigo-600" />
          <input
            type="text"
            value={query}
            onChange={(e) => resetPage(setQuery)(e.target.value)}
            placeholder="Search supplier, delivery ID, note or PO..."
            className="h-12 w-full rounded-lg border border-slate-200 bg-white pl-12 pr-4 text-sm text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </div>

        <DatePicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />

        <FilterSelect value={supplier} onChange={resetPage(setSupplier)}>
          <option>All Suppliers</option>
          {suppliers.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </FilterSelect>

        <FilterSelect value={status} onChange={resetPage(setStatus)}>
          <option>All Statuses</option>
          {statuses.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </FilterSelect>

        <button
          onClick={clearFilters}
          className="flex items-center justify-center gap-2 px-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
        >
          <FiRefreshCw className="h-4 w-4" />
          Clear filters
        </button>
      </div>

      {loading && <Loader message="Loading deliveries..." />}
      {error && <div role="alert" className="text-red-600">{error} <button onClick={refresh} className="underline">Retry</button></div>}
      {/* Table card */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
          <div className="flex items-baseline gap-3">
            <h2 className="text-lg font-bold text-slate-900">All Deliveries</h2>
            <span className="text-sm text-slate-600">
              Latest deliveries and status
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            Rows per page
            <div className="relative">
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="h-9 appearance-none rounded-md border border-slate-200 bg-white pl-3 pr-8 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none"
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-700">
                <th className="w-10 py-2.5 pl-5" />
                {HEADERS.map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-medium">
                    {h}
                  </th>
                ))}
                <th className="w-10 pr-5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={HEADERS.length + 2}
                    className="px-5 py-10 text-center text-slate-500"
                  >
                    No deliveries match your filters. Try clearing them.
                  </td>
                </tr>
              )}
              {!loading && !error && rows.map((d) => (
                <tr key={d.id} className="text-slate-800 hover:bg-slate-50/60">
                  <td className="py-3 pl-5">
                    <FiFileText className="h-5 w-5 text-indigo-600" />
                  </td>
                  <td className="px-3 py-3 leading-snug">
                    <div>{d.date}</div>
                    <div>{d.time}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3">{d.id}</td>
                  <td className="px-3 py-3">{d.supplier}</td>
                  <td className="px-3 py-3 font-medium leading-snug">
                    <div>{d.dn}</div>
                    <div>{d.po}</div>
                  </td>
                  <td className="px-3 py-3">
                    {d.products}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 font-semibold ${tempColor(
                      d.temp
                    )}`}
                  >
                    {d.temp == null ? '—' : d.temp.toFixed(1) + ' °C'}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-block whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold ${
                        STATUS_STYLES[d.status] ?? "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {d.status}
                    </span>
                    <div className="mt-1 text-xs text-slate-500">{d.outcome}</div>
                  </td>
                  <td className="px-3 py-3">{d.receivedBy}</td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => onView(d)}
                      className="rounded-md border border-indigo-200 bg-white px-8 py-1.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50"
                    >
                      View
                    </button>
                  </td>
                  <td className="pr-5">

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer / pagination */}
        <div className="relative flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4">
          <p className="text-sm text-slate-600">
            Showing {filtered.length === 0 ? 0 : start + 1} to{" "}
            {Math.min(start + rowsPerPage, filtered.length)} of{" "}
            {filtered.length} deliveries
          </p>

          <div className="flex items-center gap-2 md:absolute md:left-1/2 md:-translate-x-1/2">
            <PageButton
              label="First page"
              disabled={current === 1}
              onClick={() => setPage(1)}
            >
              <FiChevronsLeft />
            </PageButton>
            <PageButton
              label="Previous page"
              disabled={current === 1}
              onClick={() => setPage(current - 1)}
            >
              <FiChevronLeft />
            </PageButton>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => Math.abs(p - current) <= 1)
              .map((p) => (
                <PageButton
                  key={p}
                  active={p === current}
                  onClick={() => setPage(p)}
                >
                  {p}
                </PageButton>
              ))}
            <PageButton
              label="Next page"
              disabled={current === totalPages}
              onClick={() => setPage(current + 1)}
            >
              <FiChevronRight />
            </PageButton>
            <PageButton
              label="Last page"
              disabled={current === totalPages}
              onClick={() => setPage(totalPages)}
            >
              <FiChevronsRight />
            </PageButton>
          </div>
        </div>
      </div>
    </div>
  );
}